import { useEventInfo } from '@shared/utils/eventInfo';
import { useSearchAccommodationsQueryMetadataPayload } from '@shared/hooks/useSearchAccommodationsQueryMetadataPayload';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import { useEffect, useMemo, useRef } from 'react';
import { addError } from '@shared/utils/logger';
import { addAction } from '@shared/utils/logger';
import type { AccommodationCatalogUnitsOccupancyInput, TravelPlacePricingAndBookingDetailsPayload } from '@graphql/generated-base';
import { useAccommodationPlacesPricingAndBookingDetailsLazyQuery } from '../AccommodationsConsole.graphql.generated';
import { TravelCurrencyCode } from '@graphql/generated-base';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

type Parameters = Readonly<{
  dates: {
    checkIn: string;
    checkOut: string;
  };
  occupancies: AccommodationCatalogUnitsOccupancyInput;
}>;

/**
 * Hook for fetching accommodation place pricing and booking details without relying on cache.
 *
 * The previously existing pricing hook is tightly coupled with cache, which can lead to
 * stale or inconsistent pricing data, especially in the Accommodation console where up-to-date
 * pricing is critical. This hook disables caching by using the 'no-cache' fetch policy, ensuring
 * that every query returns the latest pricing and booking details from the server.
 *
 * Uses useLazyQuery to explicitly fire when all prerequisites are ready, avoiding Apollo v3
 * lifecycle issues with ssr: false + skip transitions + no-cache that can cause the query
 * result to silently arrive without triggering a React re-render.
 *
 * @param travelPlaceIds - Array of accommodation place IDs to fetch pricing for.
 * @param overrides - Optional overrides for check-in/check-out dates and occupancies.
 * @returns An object containing pricing data, loading state, error, and the effective dates.
 */

export const useAccommodationPlacesPricing = (travelPlaceIds: string[], parameters: Parameters) => {
  const { eventInfo, loading: isLoadingEventInfo } = useEventInfo();

  const { currency: currencyCode, loading: loadingTravelCurrencyAllowlist } = useTravelWhiteLabelCurrency();
  const [localeCurrencyOnOrOff, loadingUseLocaleCurrencyFlag] = useIsFeatureEnabled('localeBasedHotelCurrency');

  const { isPreparingSearchMetadataPayload, searchMetadata } = useSearchAccommodationsQueryMetadataPayload();

  const payload: TravelPlacePricingAndBookingDetailsPayload | undefined = useMemo(() => {
    if (loadingTravelCurrencyAllowlist || loadingUseLocaleCurrencyFlag || isPreparingSearchMetadataPayload || !eventInfo?.eventId) {
      return undefined;
    }

    return {
      currencyCode: (localeCurrencyOnOrOff ? currencyCode : null) ?? TravelCurrencyCode.USD,
      dates: {
        checkIn: {
          // validated with skip
          high: parameters.dates?.checkIn,
          low: parameters.dates?.checkIn,
        },
        // validated with skip
        checkOut: {
          high: parameters.dates?.checkOut,
          low: parameters.dates?.checkOut,
        },
      },
      occupancies: parameters.occupancies,
      metadata: searchMetadata,
      accommodationPlaceIds: travelPlaceIds,
    };
  }, [
    loadingTravelCurrencyAllowlist,
    loadingUseLocaleCurrencyFlag,
    isPreparingSearchMetadataPayload,
    eventInfo?.eventId,
    localeCurrencyOnOrOff,
    currencyCode,
    parameters.dates?.checkIn,
    parameters.dates?.checkOut,
    parameters.occupancies,
    searchMetadata,
    travelPlaceIds,
  ]);

  const [fetchPricing, { data, loading: travelPlacesPricingAndBookingDetailsLoading, error }] = useAccommodationPlacesPricingAndBookingDetailsLazyQuery({
    fetchPolicy: 'no-cache',
    batchMode: 'fast',
    onError(error) {
      addError(error, {
        source: 'Pricing api failed',
        context: 'travelPlacesPricingAndBookingDetails',
        errorMessage: error.message,
        checkInDate: parameters.dates?.checkIn,
        checkOutDate: parameters.dates?.checkOut,
        currencyCode: payload?.currencyCode,
        metadata: payload?.metadata,
        accommodationPlaceIds: payload?.accommodationPlaceIds,
        joyPlaceIds: payload?.joyPlaceIds,
      });
    },
    onCompleted(data) {
      const accommodationPlaces = data?.travelPlacesPricingAndBookingDetails?.accommodationPlaces;
      if (!accommodationPlaces?.length) {
        addAction('Pricing api empty results', {
          context: 'travelPlacesPricingAndBookingDetails',
          checkInDate: parameters.dates?.checkIn,
          checkOutDate: parameters.dates?.checkOut,
          currencyCode: payload?.currencyCode,
          metadata: payload?.metadata,
          accommodationPlaceIds: payload?.accommodationPlaceIds,
          joyPlaceIds: payload?.joyPlaceIds,
        });
      }
    },
  });

  // Fire the query explicitly when payload is ready and we have place IDs.
  // This avoids the Apollo v3 ssr:false + skip→active lifecycle bug where
  // no-cache queries can complete without triggering a React re-render.
  // Use a ref to track the last-fetched payload and avoid re-firing for the same input.
  const lastFetchedPayloadRef = useRef<string | null>(null);
  const payloadKey = payload ? JSON.stringify(payload) : null;
  const shouldFetch = !!payloadKey && travelPlaceIds.length > 0 && payloadKey !== lastFetchedPayloadRef.current;
  useEffect(() => {
    if (!shouldFetch || !payload || !payloadKey) {
      return;
    }
    lastFetchedPayloadRef.current = payloadKey;
    fetchPricing({ variables: { payload } });
  }, [shouldFetch, payload, payloadKey, fetchPricing]);

  // Treat the gap between "payload is ready" and "useEffect fires after paint"
  // as loading, so downstream consumers see a skeleton instead of a flash of
  // empty price state (pricingInfo.isLoading=false, values=null).
  const isLoading =
    shouldFetch ||
    travelPlacesPricingAndBookingDetailsLoading ||
    loadingTravelCurrencyAllowlist ||
    loadingUseLocaleCurrencyFlag ||
    isPreparingSearchMetadataPayload ||
    isLoadingEventInfo;

  return {
    data: data,
    loading: isLoading,
    error: error,
  };
};
